import { useEffect, useState } from "react";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { GRADE_STYLE } from "../lib/consultantGrades";

interface RateEntry {
  label:        string;
  dayPence:     number;
  hourPence:    number;
  monthPence:   number;   // managed service monthly fee; 0 = not offered at this grade
  managedDays:  number;   // consultancy days included in the managed service fee
  managedHours: number;   // consultancy hours included in the managed service fee
}

interface RateCard {
  senior:     RateEntry;
  consultant: RateEntry;
  junior:     RateEntry;
}

interface AiMarkup {
  aiMarkupBp:      number;
  aiMarkupPercent: number;
  usingOwnAi:      boolean;
}

interface CapacityData { seniorCount: number; consultantCount: number; juniorCount: number; }

const GRADE_ORDER: Array<keyof RateCard> = ["senior", "consultant", "junior"];

type GradeForm = {
  label: string; day: string; hour: string; overrideHour: boolean;
  managedOffered: boolean; month: string; managedDays: number; managedHours: number;
};
const EMPTY_GRADE: GradeForm = { label: "", day: "", hour: "", overrideHour: false, managedOffered: false, month: "", managedDays: 0, managedHours: 0 };

// Grade colours are shared with every page that shows a grade (lib/consultantGrades.ts).
const GRADE_META: Record<string, { color: string; defaultLabel: string }> = {
  senior:     { color: GRADE_STYLE.senior.color,     defaultLabel: "Senior Consultant" },
  consultant: { color: GRADE_STYLE.consultant.color, defaultLabel: "Consultant" },
  junior:     { color: GRADE_STYLE.junior.color,     defaultLabel: "Junior Consultant" },
};

const CAPACITY_GRADES: Array<{ field: keyof CapacityData; label: string; color: string }> = [
  { field: "seniorCount",     label: "Senior",     color: GRADE_STYLE.senior.color },
  { field: "consultantCount", label: "Consultant", color: GRADE_STYLE.consultant.color },
  { field: "juniorCount",     label: "Junior",     color: GRADE_STYLE.junior.color },
];

const sectionTitle: React.CSSProperties = {
  fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 4,
};
const sectionDesc: React.CSSProperties = {
  fontSize: 13, color: theme.color.subtle, marginBottom: 20, lineHeight: 1.6,
};
const fieldLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: theme.color.subtle,
  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};

function penceToGbp(p: number) { return (p / 100).toFixed(2); }
function gbpToPence(s: string): number {
  const n = parseFloat(s);
  if (isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}
function derivedHour(dayStr: string): string {
  const p = gbpToPence(dayStr);
  return p > 0 ? penceToGbp(Math.round(p / 8)) : "";
}

function toForm(e: RateEntry): GradeForm {
  return {
    label: e.label,
    day: penceToGbp(e.dayPence),
    hour: penceToGbp(e.hourPence),
    overrideHour: e.hourPence !== Math.round(e.dayPence / 8),
    managedOffered: e.monthPence > 0,
    month: e.monthPence > 0 ? (e.monthPence / 100).toFixed(0) : "",
    managedDays: e.managedDays ?? 0,
    managedHours: e.managedHours ?? 0,
  };
}

const sameRate = (a: RateEntry, b: RateEntry) =>
  a.label === b.label && a.dayPence === b.dayPence && a.hourPence === b.hourPence
  && a.monthPence === b.monthPence && a.managedDays === b.managedDays && a.managedHours === b.managedHours;

function Stepper({ value, onChange, step = 1 }: { value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button
        onClick={() => onChange(Math.max(0, +(value - step).toFixed(2)))}
        style={{ width: 36, height: 36, borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, cursor: "pointer", color: theme.color.muted, fontSize: 18, lineHeight: 1 }}
      >−</button>
      <span style={{ fontSize: 22, fontWeight: 800, color: theme.color.heading, minWidth: 32, textAlign: "center" }}>{value}</span>
      <button
        onClick={() => onChange(+(value + step).toFixed(2))}
        style={{ width: 36, height: 36, borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, cursor: "pointer", color: theme.color.muted, fontSize: 18, lineHeight: 1 }}
      >+</button>
    </div>
  );
}

export function ConsultantRatesPage() {
  const [rateCard,    setRateCard]    = useState<RateCard | null>(null);
  // The platform defaults a grade returns to when its override is removed (DELETE /consultant/rates/:grade).
  const [defaults,    setDefaults]    = useState<RateCard | null>(null);
  const [resetting,   setResetting]   = useState<string | null>(null);
  const [aiMarkup,    setAiMarkup]    = useState<AiMarkup | null>(null);
  const [capacity,    setCapacity]    = useState<CapacityData>({ seniorCount: 0, consultantCount: 0, juniorCount: 0 });
  const [loading,     setLoading]     = useState(true);

  // Per-grade form state: label + day rate (hour auto-derived, overridable)
  // plus the managed service offer (monthly fee + included days/hours).
  const [form, setForm] = useState<Record<string, GradeForm>>({
    senior: { ...EMPTY_GRADE }, consultant: { ...EMPTY_GRADE }, junior: { ...EMPTY_GRADE },
  });

  // Capacity edit state
  const [editCap,    setEditCap]    = useState<CapacityData>({ seniorCount: 0, consultantCount: 0, juniorCount: 0 });
  const [markupPercent, setMarkupPercent] = useState("30");

  // Save states
  const [savingRates,    setSavingRates]    = useState(false);
  const [savingCap,      setSavingCap]      = useState(false);
  const [savingMarkup,   setSavingMarkup]   = useState(false);
  const [savedRates,     setSavedRates]     = useState(false);
  const [savedCap,       setSavedCap]       = useState(false);
  const [savedMarkup,    setSavedMarkup]    = useState(false);
  const [ratesError,     setRatesError]     = useState("");
  const [capError,       setCapError]       = useState("");
  const [markupError,    setMarkupError]    = useState("");

  useEffect(() => {
    Promise.all([
      api.get<{ rateCard: RateCard; defaults: RateCard }>("/consultant/rates"),
      api.get<AiMarkup>("/consultant/ai-markup"),
      api.get<{ capacity: CapacityData }>("/consultant/capacity"),
    ])
      .then(([ratesRes, markupRes, capRes]) => {
        setRateCard(ratesRes.rateCard);
        setDefaults(ratesRes.defaults);
        setAiMarkup(markupRes);
        setCapacity(capRes.capacity);
        setEditCap(capRes.capacity);
        const rc = ratesRes.rateCard;
        setForm({ senior: toForm(rc.senior), consultant: toForm(rc.consultant), junior: toForm(rc.junior) });
        setMarkupPercent(String(markupRes.aiMarkupPercent));
      })
      .catch(() => setRatesError("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  function setField(grade: string, field: "label" | "day" | "hour" | "month", value: string) {
    setForm((prev) => {
      const next = { ...prev[grade], [field]: value };
      if (field === "day" && !prev[grade].overrideHour) {
        next.hour = derivedHour(value);
      }
      return { ...prev, [grade]: next };
    });
  }

  function setManaged(grade: string, patch: Partial<Pick<GradeForm, "managedOffered" | "managedDays" | "managedHours">>) {
    setForm((prev) => ({ ...prev, [grade]: { ...prev[grade], ...patch } }));
  }

  function toggleHourOverride(grade: string) {
    setForm((prev) => {
      const f = prev[grade];
      const override = !f.overrideHour;
      return { ...prev, [grade]: { ...f, overrideHour: override, hour: override ? f.hour : derivedHour(f.day) } };
    });
  }

  async function handleSaveRates() {
    setSavingRates(true); setSavedRates(false); setRatesError("");
    try {
      const body: Partial<Record<keyof RateCard, RateEntry>> = {};
      for (const grade of GRADE_ORDER) {
        const f = form[grade];
        const dayPence = gbpToPence(f.day);
        const offered  = f.managedOffered && gbpToPence(f.month) > 0;
        if (f.managedOffered && !offered) {
          setRatesError(`${f.label || GRADE_META[grade].defaultLabel}: enter a monthly fee or switch the managed service off.`);
          setSavingRates(false);
          return;
        }
        if (offered && f.managedDays <= 0 && f.managedHours <= 0) {
          setRatesError(`${f.label || GRADE_META[grade].defaultLabel}: a managed service must include some consultancy time (days or hours).`);
          setSavingRates(false);
          return;
        }
        body[grade] = {
          label:        f.label || GRADE_META[grade].defaultLabel,
          dayPence,
          hourPence:    f.overrideHour ? gbpToPence(f.hour) : Math.round(dayPence / 8),
          monthPence:   offered ? gbpToPence(f.month) : 0,
          managedDays:  offered ? f.managedDays  : 0,
          managedHours: offered ? f.managedHours : 0,
        };
      }
      const res = await api.put<{ ok: boolean; rateCard: RateCard }>("/consultant/rates", body);
      setRateCard(res.rateCard);
      setSavedRates(true);
      setTimeout(() => setSavedRates(false), 3000);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setRatesError(apiErrorMessage(err, err?.message || "Save failed"));
    } finally {
      setSavingRates(false);
    }
  }

  async function handleResetGrade(grade: keyof RateCard) {
    const label = form[grade].label || GRADE_META[grade].defaultLabel;
    if (!confirm(`Reset ${label} to the platform default rates and managed service offer? Your own figures for this grade are removed.`)) return;
    setResetting(grade); setRatesError("");
    try {
      const res = await api.del<{ ok: boolean; rateCard: RateCard }>(`/consultant/rates/${grade}`);
      setRateCard(res.rateCard);
      setForm((prev) => ({ ...prev, [grade]: toForm(res.rateCard[grade]) }));
    } catch (e: unknown) {
      setRatesError(e instanceof Error ? e.message : "Reset failed");
    } finally {
      setResetting(null);
    }
  }

  async function handleSaveCap() {
    setSavingCap(true); setSavedCap(false); setCapError("");
    try {
      const res = await api.put<{ capacity: CapacityData }>("/consultant/capacity", editCap);
      setCapacity(res.capacity);
      setSavedCap(true);
      setTimeout(() => setSavedCap(false), 3000);
    } catch {
      setCapError("Failed to save. Please try again.");
    } finally {
      setSavingCap(false);
    }
  }

  async function handleSaveMarkup() {
    setSavingMarkup(true); setSavedMarkup(false); setMarkupError("");
    try {
      const pct = parseFloat(markupPercent);
      if (isNaN(pct) || pct < 0 || pct > 100) { setMarkupError("Must be 0–100%"); setSavingMarkup(false); return; }
      const res = await api.put<AiMarkup>("/consultant/ai-markup", { aiMarkupBp: Math.round(pct * 100) });
      setAiMarkup(res);
      setSavedMarkup(true);
      setTimeout(() => setSavedMarkup(false), 3000);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setMarkupError(apiErrorMessage(err, err?.message || "Save failed"));
    } finally {
      setSavingMarkup(false);
    }
  }

  if (loading) return <LoadingBlock />;

  const totalCap = capacity.seniorCount + capacity.consultantCount + capacity.juniorCount;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Rate Card &amp; Managed Service</h1>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
        Everything a client is charged for consultant time is set here: your day and hourly rates, your managed
        service offer (monthly fee and the days included), and how many consultants are available.
      </p>
      <div style={{ margin: "0 0 28px", padding: "10px 14px", borderRadius: 3, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}44`, fontSize: 12, color: theme.color.accentText, lineHeight: 1.6 }}>
        Consultant time is a pass-through: it is billed to the client <strong>in addition to</strong> the platform fee on the Consultant-Led (L4) and Full Service (L5) tiers.
        Clients see these rates before they request either tier, and on their My Services page.
      </div>

      {/* ── Consultant grades ──────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>Time &amp; Materials Rates</div>
        <div style={sectionDesc}>Charged per day or hour booked when a client is not on a managed service. Enter the day rate — the hourly rate is calculated automatically (day ÷ 8). Override it if needed.</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {GRADE_ORDER.map((grade, idx) => {
            const f    = form[grade];
            const meta = GRADE_META[grade];
            const autoHour = derivedHour(f.day);
            return (
              <div key={grade}>
                {idx > 0 && <div style={{ height: 1, background: theme.color.border, marginBottom: 20 }} />}

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{grade}</span>
                  {rateCard && defaults && !sameRate(rateCard[grade], defaults[grade]) && (
                    <button
                      onClick={() => handleResetGrade(grade)}
                      disabled={resetting === grade}
                      title="Remove your own figures for this grade and use the platform defaults"
                      style={{ marginLeft: "auto", background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "3px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer" }}
                    >
                      {resetting === grade ? "Resetting…" : "Reset to default"}
                    </button>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", gap: 12, alignItems: "end" }}>
                  {/* Label */}
                  <div>
                    <label style={fieldLabel}>Grade label</label>
                    <input
                      style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const }}
                      value={f.label}
                      onChange={(e) => setField(grade, "label", e.target.value)}
                      placeholder={meta.defaultLabel}
                    />
                  </div>

                  {/* Day rate */}
                  <div>
                    <label style={fieldLabel}>Day rate (£)</label>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: theme.color.muted, pointerEvents: "none" }}>£</span>
                      <input
                        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const, paddingLeft: 24, fontSize: 16, fontWeight: 700 }}
                        type="number"
                        min="0"
                        step="50"
                        value={f.day}
                        onChange={(e) => setField(grade, "day", e.target.value)}
                        placeholder="850"
                      />
                    </div>
                  </div>

                  {/* Hour rate */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                      <label style={{ ...fieldLabel, marginBottom: 0 }}>Hour rate (£)</label>
                      <button
                        onClick={() => toggleHourOverride(grade)}
                        style={{ background: "none", border: "none", padding: 0, fontSize: 10, color: f.overrideHour ? theme.color.accent : theme.color.faint, cursor: "pointer", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}
                      >
                        {f.overrideHour ? "auto" : "override"}
                      </button>
                    </div>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: theme.color.muted, pointerEvents: "none" }}>£</span>
                      <input
                        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const, paddingLeft: 24, fontSize: 16, fontWeight: 700, opacity: f.overrideHour ? 1 : 0.55 }}
                        type="number"
                        min="0"
                        step="5"
                        value={f.overrideHour ? f.hour : autoHour}
                        readOnly={!f.overrideHour}
                        onChange={(e) => f.overrideHour && setField(grade, "hour", e.target.value)}
                        placeholder="106"
                      />
                    </div>
                    {!f.overrideHour && autoHour && (
                      <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 3 }}>day rate ÷ 8</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 12 }}>
          <button style={btnPrimary} onClick={handleSaveRates} disabled={savingRates}>
            {savingRates ? "Saving…" : "Save rate card"}
          </button>
          {savedRates && <span style={{ fontSize: 13, color: theme.color.successText }}>Saved</span>}
        </div>
      </div>

      {/* ── Managed service ────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>Managed Service</div>
        <div style={sectionDesc}>
          A flat monthly fee that includes a set amount of consultancy time. Offer it at any grade; clients on a
          managed service do not see your day and hourly rates, and bookings count against the included allotment.
          You can still tailor the fee or allotment for an individual client from their client page.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {GRADE_ORDER.map((grade, idx) => {
            const f    = form[grade];
            const meta = GRADE_META[grade];
            const feePence  = gbpToPence(f.month);
            const dayEquiv  = f.managedDays + f.managedHours / 8;
            const effective = feePence > 0 && dayEquiv > 0 ? Math.round(feePence / dayEquiv) : 0;
            return (
              <div key={grade}>
                {idx > 0 && <div style={{ height: 1, background: theme.color.border, marginBottom: 20 }} />}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{f.label || meta.defaultLabel}</span>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.color.text, cursor: "pointer" }}>
                    <input type="checkbox" checked={f.managedOffered} onChange={(e) => setManaged(grade, { managedOffered: e.target.checked })} />
                    Offer managed service at this grade
                  </label>
                </div>

                {f.managedOffered ? (
                  <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr", gap: 12, alignItems: "start" }}>
                    <div>
                      <label style={fieldLabel}>Monthly fee (£)</label>
                      <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: theme.color.muted, pointerEvents: "none" }}>£</span>
                        <input
                          style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const, paddingLeft: 24, fontSize: 16, fontWeight: 700 }}
                          type="number" min="0" step="50"
                          value={f.month}
                          onChange={(e) => setField(grade, "month", e.target.value)}
                          placeholder="1700"
                        />
                      </div>
                    </div>
                    <div>
                      <label style={fieldLabel}>Days included / month</label>
                      <Stepper value={f.managedDays} step={0.5} onChange={(v) => setManaged(grade, { managedDays: v })} />
                    </div>
                    <div>
                      <label style={fieldLabel}>Hours included / month</label>
                      <Stepper value={f.managedHours} onChange={(v) => setManaged(grade, { managedHours: v })} />
                    </div>
                    <div style={{ gridColumn: "1 / -1", fontSize: 11, color: theme.color.faint }}>
                      {effective > 0
                        ? <>Effective rate £{(effective / 100).toLocaleString("en-GB", { maximumFractionDigits: 0 })}/day across {dayEquiv} day{dayEquiv !== 1 ? "s" : ""} (T&amp;M day rate £{f.day || "—"})</>
                        : "Enter the monthly fee and the consultancy time it includes."}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: theme.color.faint }}>Not offered — clients at this grade are billed time &amp; materials only.</div>
                )}
              </div>
            );
          })}
        </div>

        {ratesError && (
          <div style={{ background: theme.color.dangerBg, color: theme.color.dangerText, borderRadius: 3, padding: "10px 14px", fontSize: 13, marginTop: 20 }}>
            {ratesError}
          </div>
        )}

        <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 12 }}>
          <button style={btnPrimary} onClick={handleSaveRates} disabled={savingRates}>
            {savingRates ? "Saving…" : "Save rate card"}
          </button>
          {savedRates && <span style={{ fontSize: 13, color: theme.color.successText }}>Saved — rates and managed service offer updated</span>}
        </div>
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8 }}>Saves both the time &amp; materials rates above and the managed service offer.</div>
      </div>

      {/* ── Consultant availability ────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>Available Consultants</div>
        <div style={sectionDesc}>
          Set how many consultants of each grade are available per working day. This caps how many bookings clients can make across all your clients, and is shown to clients as your available capacity.
          {totalCap > 0 && (
            <span style={{ display: "block", marginTop: 4, fontSize: 12, color: theme.color.faint }}>
              Current: {capacity.seniorCount} senior · {capacity.consultantCount} consultant · {capacity.juniorCount} junior ({totalCap} total · {totalCap * 8} hrs/day)
            </span>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 20 }}>
          {CAPACITY_GRADES.map(({ field, label, color }) => (
            <div key={field} style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "16px 18px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>{label}</div>
              <Stepper value={editCap[field]} onChange={(v) => setEditCap((c) => ({ ...c, [field]: v }))} />
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8 }}>
                {editCap[field] * 8} hrs/day capacity
              </div>
            </div>
          ))}
        </div>

        {capError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 10 }}>{capError}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button style={btnPrimary} onClick={handleSaveCap} disabled={savingCap}>
            {savingCap ? "Saving…" : "Save availability"}
          </button>
          {savedCap && <span style={{ fontSize: 13, color: theme.color.successText }}>Saved</span>}
        </div>
      </div>

      {/* ── AI markup ─────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={sectionTitle}>AI Usage Markup</div>
        <div style={sectionDesc}>
          {aiMarkup?.usingOwnAi
            ? "Your consultancy is using its own AI provider — this markup does not apply."
            : "When clients use the platform's built-in AI, their usage is billed with this markup. Set to 0% to pass through at cost."}
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
          <div style={{ maxWidth: 140 }}>
            <label style={fieldLabel}>Markup %</label>
            <input
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const, opacity: aiMarkup?.usingOwnAi ? 0.5 : 1 }}
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={markupPercent}
              disabled={!!aiMarkup?.usingOwnAi}
              onChange={(e) => setMarkupPercent(e.target.value)}
              placeholder="30"
            />
          </div>
          <button
            style={{ ...btnPrimary, opacity: aiMarkup?.usingOwnAi ? 0.5 : 1 }}
            onClick={handleSaveMarkup}
            disabled={savingMarkup || !!aiMarkup?.usingOwnAi}
          >
            {savingMarkup ? "Saving…" : "Save markup"}
          </button>
          {savedMarkup && <span style={{ fontSize: 13, color: theme.color.successText }}>Saved</span>}
        </div>

        {markupError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 10 }}>{markupError}</div>}

        {!aiMarkup?.usingOwnAi && (
          <p style={{ fontSize: 12, color: theme.color.faint, margin: "10px 0 0" }}>
            Platform default is 30%. Current: {aiMarkup ? `${aiMarkup.aiMarkupPercent}%` : "—"}
          </p>
        )}
      </div>
    </div>
  );
}
